import React, { useState } from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Box, Button, Chip, IconButton, Switch, FormControl, OutlinedInput, InputAdornment } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import BackupIcon from '@mui/icons-material/Backup';
import axios from 'axios';

import { useFetchData } from '../../../../hooks/useFetchData/useFetchData';
import { useShowMessages } from '../../../../hooks/useMessages/useMessages';
import CustomTableCell from '../../../shared/CustomTable/CustomTableCell';

const url = process.env.REACT_APP_API + '/repositories';

export default function RepositoriesTable(props) {
  const { data: repositories } = useFetchData(url, {}, props.changed);
  const { ShowMessage } = useShowMessages();
  const [toggling, setToggling] = useState(null);
  const [search, setSearch] = useState('');
  const [backingUp, setBackingUp] = useState(false);

  const handleToggle = async (repo) => {
    setToggling(repo.id);
    try {
      await axios.patch(`${url}/${repo.id}/toggle`);
      props.handleChanged();
    } catch (error) {
      ShowMessage('error', 'Erro ao alterar status do repositório');
    } finally {
      setToggling(null);
    }
  };

  const handleBackup = async () => {
    setBackingUp(true);
    try {
      const response = await axios.post(`${url}/backup`);
      ShowMessage('success', response.data.message || 'Backup iniciado com sucesso!');
    } catch (error) {
      ShowMessage('error', 'Erro ao disparar backup');
    } finally {
      setBackingUp(false);
    }
  };

  const repos = repositories?.repositories || [];
  const filteredRepos = repos.filter((repo) => {
    const term = search.toLowerCase();
    return repo.name?.toLowerCase().includes(term) || repo.url?.toLowerCase().includes(term);
  });

  return (
    <Box sx={{ width: '100%', padding: 1 }}>
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <FormControl variant="outlined" size="small" fullWidth>
          <OutlinedInput
            placeholder="Pesquisar"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            endAdornment={
              <InputAdornment position="end">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            }
          />
        </FormControl>
        <Button
          variant="contained"
          startIcon={<BackupIcon />}
          onClick={handleBackup}
          disabled={backingUp}
          sx={{ whiteSpace: 'nowrap', minWidth: 220 }}
        >
          {backingUp ? 'Disparando...' : 'Disparar Backup'}
        </Button>
      </Box>
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ overflow: 'scroll', height: '70vh' }}>
          <Table stickyHeader sx={{ width: '100%' }}>
            <TableHead>
              <TableRow>
                <CustomTableCell>ID</CustomTableCell>
                <CustomTableCell>Nome</CustomTableCell>
                <CustomTableCell>URL</CustomTableCell>
                <CustomTableCell align="center">Status</CustomTableCell>
                <CustomTableCell align="center">Ativo</CustomTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRepos.map((row) => (
                <TableRow key={row.id}>
                  <CustomTableCell component="th" scope="row">{row.id}</CustomTableCell>
                  <CustomTableCell>{row.name}</CustomTableCell>
                  <CustomTableCell>
                    {row.url}
                    <IconButton size="small" onClick={() => window.open(row.url, '_blank')} title="Abrir no GitHub">
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </CustomTableCell>
                  <CustomTableCell align="center">
                    <Chip
                      label={row.enabled ? 'Ativo' : 'Inativo'}
                      color={row.enabled ? 'success' : 'default'}
                      size="small"
                    />
                  </CustomTableCell>
                  <CustomTableCell align="center">
                    <Switch
                      checked={row.enabled}
                      onChange={() => handleToggle(row)}
                      disabled={toggling === row.id}
                      size="small"
                    />
                  </CustomTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
